import React, { useRef, useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './VideoSettingsDialog.module.css';
import { Icon } from '../../Icons';
import { handleGetUserMediaError } from '../../../utils/media';
import VideoManipulator from '../../../utils/VideoManipulator';
import AudioManipulator from '../../../utils/AudioManipulator';
import VideoSettingsVA from './VideoSettingsVA';
import VideoSettingsEffects from './VideoSettingsEffects';
import VideoSettingsLanguage from './VideoSettingsLanguage';
import useAudio from '../../../hooks/useAudio';
import useIsMobile from '../../../hooks/useIsMobile';
import { ALLOW_VIDEO_MANIPULATION } from '../../../utils/media';
import BrowserNotSupported from '../../Modals/BrowserNotSupported';
import { isBrowserSupported } from '../../../utils/browserCheck';
import { stringAvatar } from '../../../utils/avatar';
import RoomContext from '../../../lib/RoomClientContext';
import {
  setShowSettings,
  setShowAdvancedSettings,
  setAudioLevel,
} from '../../../store/slices/deviceSlice';
import {
  setVideoDenied,
  setAudioDenied,
  setCameraTaken,
  setVideoDismissed,
  setAudioDismissed,
} from '../../../store/slices/permissionSlice';

function ToggleDivider({ open, onToggle, t }) {
  return (
    <div className={styles.toggleDivider} onClick={() => onToggle(!open)}>
      <div className={styles.divider} />
      <p className={styles.toggleText}>{t('More functions')}</p>
      <Icon
        className={styles.toggleIcon}
        name={open ? 'caretUp' : 'caretDown'}
        width={30}
        height={30}
      />
    </div>
  );
}

function VideoSettingsDialogContent({ useAspectRatio }) {
  const roomClient = useContext(RoomContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setSink: setTestSink } = useAudio();
  const { isMobile } = useIsMobile();
  const token = useSelector((state) => state.user.token);
  const userData = useSelector((state) => state.user.userData);
  const deviceLoaded = useSelector((state) => state.device.deviceLoaded);
  const deviceSetup = useSelector((state) => state.device.deviceSetup);
  const deviceSettings = useSelector((state) => state.device.deviceSettings);
  const showSettings = useSelector((state) => state.device.showSettings);
  const clientOptions = useSelector((state) => state.user.clientOptions);
  const audioManipulation = useSelector(
    (state) => state.device.audioManipulation
  );
  const showAdvancedSettings = useSelector(
    (state) => state.device.showAdvancedSettings
  );
  const hideVideo = useSelector((state) => state.control.hideVideo);

  const checkedPermission = useSelector(
    (state) => state.permission.checkedPermission
  );
  const videoDenied = useSelector((state) => state.permission.videoDenied);
  const cameraTaken = useSelector((state) => state.permission.cameraTaken);
  const [videoDevice, setActiveVideoDevice] = useState(
    deviceSettings.videoDevice
  );
  const [audioDevice, setActiveAudioDevice] = useState(
    deviceSettings.audioDevice
  );
  const [outputDevice, setOutputDevice] = useState(
    deviceSettings.outputDevice || 'notsupported'
  );
  const [videoEffect, setVideoEffect] = useState(deviceSettings.videoEffect);
  const [loading, setLoading] = useState(false);
  const [availableVideoDevices, setAvailableVideoDevices] = useState([]);
  const [availableAudioDevices, setAvailableAudioDevices] = useState([]);
  const [availableOutputDevices, setAvailableOutputDevices] = useState([]);
  const [videoStream, setVideoStream] = useState(null);
  const [videoCanvas, setVideoCanvas] = useState(null);
  const videoManipulatorRef = useRef(null);
  const audioManipulatorRef = useRef(null);

  const showJoinAction = !deviceSetup;
  const saveButtonText = showJoinAction ? 'Join Meeting' : 'Save';
  const usingForcedEffect = clientOptions?.forceVirtualBackgroundUrl;

  const device = roomClient.device;
  const open = showSettings; // determines whether the dialog is open
  const allowVideoManipulation =
    (ALLOW_VIDEO_MANIPULATION || clientOptions?.forceVirtualBackgroundUrl) &&
    !clientOptions?.disableVisualEffects;

  useEffect(() => {
    if (open) {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (outputDevice === 'notsupported') {
      return;
    }
    setTestSink(outputDevice);
  }, [outputDevice, setTestSink]);

  useEffect(() => {
    if (clientOptions?.disableCamera) {
      setActiveVideoDevice('nocam');
    }
    if (clientOptions?.disableMicrophone) {
      setActiveAudioDevice('nomic');
    }
  }, [clientOptions]);

  useEffect(() => {
    if (!checkedPermission || !open) return;
    let stream;
    (async () => {
      if (videoDevice === 'nocam') {
        if (videoManipulatorRef.current) {
          videoManipulatorRef.current.destroy();
          videoManipulatorRef.current = null;
        }
        setVideoStream(null);
        return;
      }
      try {
        stream = await roomClient.streamHandler.getVideoStream({
          device,
          token,
          videoDevice,
          isMobile,
          throwError: true,
        });

        if (stream) {
          videoManipulatorRef.current = new VideoManipulator(
            stream,
            !allowVideoManipulation
          );
          if (clientOptions?.forceVirtualBackgroundUrl) {
            videoManipulatorRef.current.setEffect(
              {
                type: 'virtual-background',
                image: clientOptions.forceVirtualBackgroundUrl,
              },
              dispatch
            );
          } else {
            videoManipulatorRef.current.setEffect(videoEffect, dispatch);
          }
          setVideoCanvas(videoManipulatorRef.current.getCanvas());
          const o = videoManipulatorRef.current.getOutputStream();
          o.force = true;
          setVideoStream(o);
          dispatch(setCameraTaken(false));
          dispatch(setVideoDenied(false));
          dispatch(setVideoDismissed(false));
        } else {
          dispatch(setVideoDenied(true));
        }
      } catch (e) {
        handleGetUserMediaError(e, {
          onSystemRejection: () => dispatch(setCameraTaken(true)),
          onCameraTaken: () => dispatch(setCameraTaken(true)),
          onUserDenied: () => dispatch(setVideoDenied(true)),
          onUserDismissed: () => dispatch(setVideoDismissed(true)),
        });
      }
    })();

    return () => {
      if (videoManipulatorRef.current) {
        videoManipulatorRef.current.destroy();
        videoManipulatorRef.current = null;
      }

      setVideoCanvas(null);
      setVideoStream(null);
    };
  }, [checkedPermission, open, videoDevice, device, allowVideoManipulation]);

  useEffect(() => {
    if (!checkedPermission || !open) return;
    let stream;
    (async () => {
      if (audioDevice === 'nomic') {
        if (audioManipulatorRef.current) {
          audioManipulatorRef.current.destroy();
          audioManipulatorRef.current = null;
        }
        return;
      }
      try {
        stream = await roomClient.streamHandler.getAudioStream({
          device,
          token,
          audioDevice,
          isMobile,
        });
        if (stream) {
          dispatch(setAudioLevel(0));
          audioManipulatorRef.current = new AudioManipulator(
            stream,
            deviceSettings?.audioManipulation
          );
          await audioManipulatorRef.current.init();
          audioManipulatorRef.current.activateVad();
          audioManipulatorRef.current.on('level', (level) => {
            const newLevel = parseInt(level * 100, 10);
            dispatch(setAudioLevel(newLevel));
          });
          dispatch(setAudioDenied(false));
          dispatch(setAudioDismissed(false));
        } else {
          dispatch(setAudioDenied(true));
        }
      } catch (e) {
        handleGetUserMediaError(e, {
          onUserDenied: () => dispatch(setAudioDenied(true)),
          onUserDismissed: () => dispatch(setAudioDismissed(true)),
        });
      }
    })();

    return () => {
      if (audioManipulatorRef.current) {
        audioManipulatorRef.current.destroy();
        audioManipulatorRef.current = null;
      }
    };
  }, [checkedPermission, open, audioDevice, device]);

  useEffect(() => {
    if (!checkedPermission || usingForcedEffect) return;
    videoManipulatorRef.current?.setEffect(videoEffect, dispatch);
  }, [videoEffect, checkedPermission, usingForcedEffect]);

  function onSave() {
    roomClient.onCloseSettings({
      videoDevice,
      audioDevice,
      outputDevice,
      videoEffect,
      audioManipulation,
    });
    setLoading(true);
    roomClient.joinMeeting();
    roomClient.getUsersInQueue();
  }

  const videoWidth = isMobile ? window.innerWidth : 600;
  const videoHeight = videoWidth / (16 / 9);

  useEffect(() => {
    if (!showJoinAction) return;
    // only relevant when not yet joined
    const handlePreferredDevice = (
      availableDevices,
      preferredDeviceInfo,
      onPreferred
    ) => {
      const preferredDevice = availableDevices.find(
        (device) =>
          device.deviceId === preferredDeviceInfo?.deviceId ||
          device.label === preferredDeviceInfo?.label
      );
      if (preferredDevice) onPreferred(preferredDevice.deviceId);
      return preferredDevice ? preferredDevice.deviceId : null;
    };
    const preferredDevices = {
      audio: handlePreferredDevice(
        availableAudioDevices,
        clientOptions?.devices?.audio,
        setActiveAudioDevice
      ),
      video: handlePreferredDevice(
        availableVideoDevices,
        clientOptions?.devices?.video,
        setActiveVideoDevice
      ),
      output: handlePreferredDevice(
        availableOutputDevices,
        clientOptions?.devices?.audioOutput,
        setOutputDevice
      ),
    };
    // if all preferred devices are set, join the meeting
    if (
      preferredDevices.audio &&
      preferredDevices.video &&
      preferredDevices.output
    ) {
      onSave();
    }
  }, [availableAudioDevices, availableOutputDevices, availableVideoDevices]);

  if (!deviceLoaded) {
    return (
      <Dialog
        PaperProps={{
          className: classnames(styles.dialog, { [styles.isMobile]: isMobile }),
        }}
        open={open}
      >
        <div
          className={classnames(styles.dialogContent, styles.avatarContainer)}
        >
          <CircularProgress size={30} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      PaperProps={{
        className: classnames(styles.dialog, { [styles.isMobile]: isMobile }),
      }}
      open={open}
      onClose={(e, reason) => {
        if (showJoinAction || loading) return false;
        dispatch(setShowSettings(false));
      }}
      disableEscapeKeyDown={showJoinAction || loading}
    >
      <DialogContent className={styles.dialogContent} dividers>
        <div className={styles.container} id="settings-dialog">
          <div className={styles.content}>
            <div className={styles.videoContainer}>
              {!videoDenied && cameraTaken && (
                <div className={styles.cameraTakenText}>
                  <p>
                    {t(
                      `Unable to preview your camera. If it doesn't update after clicking '${saveButtonText},' another application may be using it.`
                    )}
                  </p>
                </div>
              )}
              {device &&
              !hideVideo &&
              videoCanvas &&
              !clientOptions.disableCamera ? (
                allowVideoManipulation ? (
                  <div
                    key="canvas"
                    id="video-preview"
                    ref={(ref) => {
                      if (ref) {
                        ref.innerHTML = '';
                        videoCanvas.style.width = '100%';
                        videoCanvas.style.height = '100%';
                        videoCanvas.style.background = '#333';
                        videoCanvas.style.objectFit = useAspectRatio
                          ? 'contain'
                          : 'cover';
                        if (!clientOptions?.disableMirroredCamera) {
                          videoCanvas.style.transform = 'scaleX(-1)';
                        }
                        videoCanvas.style['max-height'] = '280px';
                        ref.appendChild(videoCanvas);
                      }
                    }}
                  />
                ) : (
                  <video
                    ref={(ref) => {
                      if (
                        ref &&
                        ((!ref.srcObject && videoStream) ||
                          (ref.srcObject && !videoStream) ||
                          videoStream?.force)
                      ) {
                        ref.srcObject = videoStream;
                        ref.style.width = '100%';
                        ref.style.height = '100%';
                        ref.style.background = '#333';
                        ref.style.objectFit = useAspectRatio
                          ? 'contain'
                          : 'cover';
                        if (!clientOptions?.disableMirroredCamera) {
                          ref.style.transform = 'scaleX(-1)';
                        }
                        ref.style['max-height'] = '280px';
                        if (videoStream?.force) videoStream.force = false;
                      }
                    }}
                    playsInline
                    autoPlay
                    controls={false}
                    muted
                  />
                )
              ) : (
                <div
                  key="avatar"
                  className={styles.avatarContainer}
                  style={{ minHeight: Math.min(280, videoHeight) }}
                >
                  <Avatar
                    {...stringAvatar(userData?.meetingName, {
                      width: 100,
                      height: 100,
                    })}
                    className={styles.avatar}
                  />
                </div>
              )}
            </div>
            <div className={styles.scrollable}>
              <h3 className={styles.sectionTitle}>{t('Settings')}</h3>
              <VideoSettingsVA
                videoDevice={videoDevice}
                setVideoDevice={setActiveVideoDevice}
                audioDevice={audioDevice}
                setAudioDevice={setActiveAudioDevice}
                outputDevice={outputDevice}
                setOutputDevice={setOutputDevice}
                deviceSettings={deviceSettings}
                device={device}
                clientOptions={clientOptions}
                streamHandler={roomClient.streamHandler}
                availableVideoDevices={availableVideoDevices}
                setAvailableVideoDevices={setAvailableVideoDevices}
                availableAudioDevices={availableAudioDevices}
                setAvailableAudioDevices={setAvailableAudioDevices}
                availableOutputDevices={availableOutputDevices}
                setAvailableOutputDevices={setAvailableOutputDevices}
              />
              <ToggleDivider
                onToggle={(state) => dispatch(setShowAdvancedSettings(state))}
                open={showAdvancedSettings}
                t={t}
              />
              {showAdvancedSettings ? (
                <>
                  {/* <div className={styles.divider} /> */}
                  <VideoSettingsLanguage />
                  <VideoSettingsEffects
                    videoEffect={videoEffect}
                    setVideoEffect={setVideoEffect}
                    isMobile={isMobile}
                    allowVideoManipulation={allowVideoManipulation}
                    usingForcedEffect={usingForcedEffect}
                  />
                  {/* {showJoinAction ? (
                    <>
                      <div className={styles.divider} />
                      <h3 className={styles.sectionTitle}>
                        {t('Advanced settings')}
                      </h3>

                      <AudioManipulatorSettings
                        onToggleAudioManipulation={onToggleAudioManipulation}
                      />
                    </>
                  ) : null} */}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
      {loading ? (
        <DialogActions
          className={classnames(styles.actions, styles.singleAction)}
        >
          <Button
            color="white"
            variant="contained"
            className={styles.button}
            disableRipple
            disableFocusRipple
            id="video-settings-loading"
          >
            <CircularProgress size={30} />
          </Button>
        </DialogActions>
      ) : (
        <DialogActions
          className={classnames(styles.actions, styles.singleAction)}
        >
          {!showJoinAction && (
            <Button
              color="white"
              className={styles.button}
              onClick={() => dispatch(setShowSettings(false))}
              id="video-settings-cancel"
            >
              {t('Cancel')}
            </Button>
          )}
          <Button
            color="white"
            variant="contained"
            className={styles.button}
            onClick={() => onSave()}
            id="video-settings-save"
          >
            {t(saveButtonText)}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function VideoSettingsDialog() {
  const showSettings = useSelector((state) => state.device.showSettings);
  const browserSupported = isBrowserSupported();
  const [showBrowserMessage, setShowBrowserMessage] = useState(
    !browserSupported
  );

  const handleContinue = () => {
    setShowBrowserMessage(false);
  };

  return (
    <>
      {showSettings && showBrowserMessage ? (
        <BrowserNotSupported onContinue={handleContinue} />
      ) : (
        <VideoSettingsDialogContent useAspectRatio />
      )}
    </>
  );
}

export default React.memo(VideoSettingsDialog);
